import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  EmptyState, Input, LoadingState, Toast,
} from '@biznomi/ui-react'
import { getLeaves, approveLeave, rejectLeave, leaveTypeLabel, type LeaveRecord } from '@/lib/api/leave'

/** 고른 날짜를 그대로 보여준다. 옛 기록은 dates 가 없어 기간으로 */
function daysText(r: LeaveRecord): string {
  if (r.dates?.length) return r.dates.map((d) => d.slice(5, 10).replace('-', '/')).join(', ')
  const s = r.startDate.slice(5, 10).replace('-', '/')
  const e = r.endDate.slice(5, 10).replace('-', '/')
  return s === e ? s : `${s} ~ ${e}`
}

/**
 * 휴가 승인·반려.
 *
 * 잔여가 모자라도 시스템은 막지 않는다 — 선사용·특별휴가는 정상 운영이다.
 * 판단은 관리자가 한다. 우리는 사실만 보여준다 (§1 경계).
 */
export default function LeaveApprovals() {
  const qc = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')
  const [rejecting, setRejecting] = useState<LeaveRecord | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['leave', 'pending'],
    queryFn: () => getLeaves({ status: 'PENDING' }),
  })
  const rows = data?.success ? data.data ?? [] : []

  async function doApprove(r: LeaveRecord) {
    setBusy(r.id); setError('')
    const res = await approveLeave(r.id)
    setBusy(null)
    if (!res.success) { setError(res.error ?? '승인에 실패했습니다'); return }
    qc.invalidateQueries({ queryKey: ['leave'] })
    setToast('승인했습니다')
  }

  async function doReject() {
    if (!rejecting) return
    if (!rejectReason.trim()) { setError('반려 사유를 적어주세요'); return }
    setBusy(rejecting.id); setError('')
    const res = await rejectLeave(rejecting.id, rejectReason.trim())
    setBusy(null)
    if (!res.success) { setError(res.error ?? '반려에 실패했습니다'); return }
    setRejecting(null); setRejectReason('')
    qc.invalidateQueries({ queryKey: ['leave'] })
    setToast('반려했습니다')
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">휴가 승인 대기</h1>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="대기 중인 휴가 신청이 없습니다" />
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} className="rounded-card bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-semibold">{r.employee?.name ?? '—'}</span>
                    <Badge variant="purple">{leaveTypeLabel(r.type)}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{daysText(r)}</p>
                  {r.reason && <p className="text-xs text-slate-400">{r.reason}</p>}
                </div>
                {/*
                  옛 기록은 dates·days 가 없다. 0 이라고 우기면 거짓이 되므로 모른다고 쓴다.
                  승인하면 서버가 달력 일수로 센다 (하위 호환 경로).
                */}
                <span className="shrink-0 font-bold tabular-nums">
                  {r.days ?? (r.dates?.length || null) ?? '?'}일
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  onClick={() => { setRejecting(r); setRejectReason(''); setError('') }}
                >
                  반려
                </Button>
                <Button loading={busy === r.id} onClick={() => void doApprove(r)}>
                  승인
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* 반려 사유는 필수다 — 직원이 그대로 보는 유일한 설명이라 빈 반려는 막는다 */}
      <Dialog
        open={rejecting !== null}
        onOpenChange={(o) => { if (!o) { setRejecting(null); setRejectReason('') } }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>반려 사유를 알려주세요</DialogTitle>
            <DialogDescription>
              {rejecting?.employee?.name ?? ''} 님이 휴가 화면에서 이 문구를 그대로 보게 됩니다.
            </DialogDescription>
          </DialogHeader>

          <Input
            autoFocus
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            maxLength={200}
            placeholder="예: 해당 기간 인력이 부족합니다"
          />

          <DialogFooter>
            <Button
              variant="secondary" className="flex-1"
              onClick={() => { setRejecting(null); setRejectReason('') }}
              disabled={busy !== null}
            >
              취소
            </Button>
            <Button
              variant="destructive" className="flex-1"
              disabled={!rejectReason.trim()}
              loading={busy !== null}
              onClick={() => void doReject()}
            >
              반려
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}
